import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from 'services/api';

const makeStatusParams = (status) => {
  const initialStatus = {
    isSuccess: false,
    isError: false,
    isLoading: false,
  };
  if (status === 'pending') return { ...initialStatus, isLoading: true };
  if (status === 'error') return { ...initialStatus, isError: true };
  if (status === 'success') return { ...initialStatus, isSuccess: true };
  return initialStatus;
};

export const defaultConfig = {
  clientQueryParameterName: 'clientId',
  immediate: true,
  accountChange: false,
  retry: false,
  retriesCount: 5,
  shouldRetry: null,
  retryDelay: 1000,
  backoffFactor: 2,
  defaultState: {
    status: 'idle',
    value: null,
    error: null,
    isInitialRequest: false,
  },
  onSuccess: null,
  onError: null,
  onComplete: null,
};

/**
 * Sleep with abort signal support
 */
const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    const timeout = setTimeout(resolve, ms);
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timeout);
          reject(new Error('Aborted during delay'));
        },
        { once: true }
      );
    }
  });

/**
 * Executes a single async function with retry logic and abort signal support.
 */
const executeWithRetry = async (fn, args, config, signal) => {
  const { retry, retriesCount, retryDelay, backoffFactor, shouldRetry } = config;

  let attempt = 0;
  while (true) {
    if (signal?.aborted) throw new Error('Aborted before execution');

    try {
      // Pass signal as last param so functions can handle it if they accept it
      return await fn(...args, signal);
    } catch (error) {
      if (signal?.aborted) throw new Error('Aborted during execution');

      if (!retry || attempt >= retriesCount) {
        throw error;
      }

      attempt += 1;
      const delay = retryDelay * Math.pow(backoffFactor, attempt - 1);
      const statusCode = error?.response?.status ?? 0;

      // Allow callback-based retry handling
      if (typeof shouldRetry === 'function' && !shouldRetry(statusCode)) {
        throw error;
      }

      console.warn(`Retrying step (attempt ${attempt}/${retriesCount}) in ${delay}ms — status: ${statusCode}`);

      await sleep(delay, signal);
    }
  }
};

/**
 * Executes a sequence of async functions sequentially,
 * each with per-step retry and abort signal support.
 */
const executeSequentiallyWithRetry = async (functions, config, signal, ...args) => {
  let result = args;

  for (let i = 0; i < functions.length; i++) {
    if (signal?.aborted) throw new Error('Aborted before next step');
    const fn = functions[i];
    const params = Array.isArray(result) ? result : [result];
    result = await executeWithRetry(fn, params, config, signal);
  }

  return result;
};

export const useAsyncNew = (asyncServiceOrArray, config = defaultConfig) => {
  const {
    immediate,
    accountChange,
    defaultState,
    onSuccess,
    onError,
    onComplete,
    retry,
    retriesCount,
    shouldRetry,
    retryDelay,
    backoffFactor,
  } = {
    ...defaultConfig,
    ...config,
    defaultState: { ...defaultConfig.defaultState, ...config.defaultState },
  };

  const [status, setStatus] = useState(defaultState.status);
  const [value, setValue] = useState(defaultState.value);
  const [error, setError] = useState(defaultState.error);

  const [hasRequested, setHasRequested] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  const requestIdRef = useRef(0);
  const abortControllerRef = useRef(null);
  const isAccountChanged = useRef(false);
  const lastRequest = useRef(Promise.reject);

  useEffect(() => {
    isAccountChanged.current = true;
    return () => {
      isAccountChanged.current = false;
    };
  }, [accountChange]);

  const execute = useCallback(
    (...args) => {
      const currentRequestId = ++requestIdRef.current;
      const serviceChain = Array.isArray(asyncServiceOrArray) ? asyncServiceOrArray : [asyncServiceOrArray];

      const runAsync = async () => {
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        api.defaults.signal = signal;

        if (!hasRequested) setHasRequested(true);
        setStatus('pending');
        setValue(null);
        setError(null);

        let currentData = null;
        let currentError = null;
        let currentStatus = 'pending';

        try {
          const response = await executeSequentiallyWithRetry(
            serviceChain,
            {
              retry,
              retriesCount,
              shouldRetry,
              retryDelay,
              backoffFactor,
            },
            signal,
            ...args
          );

          if (currentRequestId !== requestIdRef.current) return;

          isAccountChanged.current = false;
          setValue(response);
          setStatus('success');
          setHasLoadedOnce(true);
          currentData = response;
          currentStatus = 'success';
          if (typeof onSuccess === 'function') onSuccess(response);
        } catch (err) {
          if (currentRequestId !== requestIdRef.current) return;

          if (signal.aborted) {
            console.warn('Execution aborted');
          }

          isAccountChanged.current = false;
          setError(err);
          setStatus('error');
          setHasLoadedOnce(true);
          currentError = err;
          currentStatus = 'error';
          if (typeof onError === 'function') onError(err);
        } finally {
          if (currentRequestId === requestIdRef.current && typeof onComplete === 'function') {
            onComplete({ data: currentData, error: currentError, ...makeStatusParams(currentStatus) });
          }
        }
      };

      lastRequest.current = () => execute(...args);
      return runAsync();
    },
    [asyncServiceOrArray]
  );

  const reset = (props = {}) => {
    const stateProps = { ...defaultState, ...props };
    setStatus(stateProps.status);
    setValue(stateProps.value);
    setError(stateProps.error);
  };

  useEffect(() => {
    if (immediate) execute();
    return () => abortControllerRef.current?.abort();
  }, [execute, immediate]);

  return {
    reset,
    execute,
    data: value,
    status,
    error,
    ...makeStatusParams(status),
    isInitialRequest: !hasLoadedOnce,
    isAccountChanged: isAccountChanged.current,
    lastRequest: lastRequest.current,
    abort: () => abortControllerRef.current?.abort(),
  };
};
